import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function AboutPage() {
  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1624816804260-65ea43f9615e?auto=format&fit=crop&w=1920&q=80')`
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 text-center container-custom">
          <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Our Story</span>
          <h1 className="font-['Syne'] text-4xl md:text-6xl font-bold mt-4">
            About LostNFound860
          </h1>
        </div>
      </section>

      {/* Story Section */}
      <section className="section container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#FF3B30] uppercase text-xs tracking-widest">The Beginning</span>
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold mt-2 mb-6">
              Born from the Streets
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              LostNFound860 was born from a simple idea: premium streetwear shouldn't just be for 
              individuals—it should bring families together. Founded with a passion for urban culture 
              and a commitment to quality, we set out to create clothing that transcends age.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              The "860" in our name represents our roots, our area code, and our commitment to 
              staying authentic. We're not just making clothes; we're building a community of 
              families who express their style together.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Every piece we create goes through rigorous quality control. We use premium materials, 
              ethical manufacturing practices, and designs that stand the test of time.
            </p>
          </div>
          <div className="aspect-square border border-neutral-800 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1556821840-3a63f95609a7?auto=format&fit=crop&w=800&q=80"
              alt="Our Story"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-neutral-900/50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-[#FF3B30] uppercase text-xs tracking-widest">What We Stand For</span>
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold mt-2">Our Values</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="border border-neutral-800 p-8 text-center">
              <div className="w-16 h-16 border border-white flex items-center justify-center mx-auto mb-6">
                <span className="font-['Syne'] text-2xl font-bold">01</span>
              </div>
              <h3 className="font-['Syne'] text-xl font-bold mb-4">Quality First</h3>
              <p className="text-muted-foreground text-sm">
                We never compromise on materials or craftsmanship. Every stitch, every fabric, 
                every detail is carefully considered.
              </p>
            </div>

            <div className="border border-neutral-800 p-8 text-center">
              <div className="w-16 h-16 border border-white flex items-center justify-center mx-auto mb-6">
                <span className="font-['Syne'] text-2xl font-bold">02</span>
              </div>
              <h3 className="font-['Syne'] text-xl font-bold mb-4">Family Unity</h3>
              <p className="text-muted-foreground text-sm">
                Style has no age limit. We design for kids, adults, and everyone in between—
                because families that match, stay together.
              </p>
            </div>

            <div className="border border-neutral-800 p-8 text-center">
              <div className="w-16 h-16 border border-white flex items-center justify-center mx-auto mb-6">
                <span className="font-['Syne'] text-2xl font-bold">03</span>
              </div>
              <h3 className="font-['Syne'] text-xl font-bold mb-4">Street Culture</h3>
              <p className="text-muted-foreground text-sm">
                Our roots are in urban culture. We celebrate the creativity, the hustle, 
                and the community that makes street fashion what it is.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section container-custom">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div>
            <span className="font-['Syne'] text-4xl md:text-5xl font-bold block">860</span>
            <span className="text-muted-foreground uppercase text-xs tracking-widest">Our Area Code</span>
          </div>
          <div>
            <span className="font-['Syne'] text-4xl md:text-5xl font-bold block">100%</span>
            <span className="text-muted-foreground uppercase text-xs tracking-widest">Premium Quality</span>
          </div>
          <div>
            <span className="font-['Syne'] text-4xl md:text-5xl font-bold block">3</span>
            <span className="text-muted-foreground uppercase text-xs tracking-widest">Generations</span>
          </div>
          <div>
            <span className="font-['Syne'] text-4xl md:text-5xl font-bold block text-[#FF3B30]">1</span>
            <span className="text-muted-foreground uppercase text-xs tracking-widest">Family</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-custom">
          <div className="border border-neutral-800 p-12 md:p-20 text-center">
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold mb-6">
              Join the Family
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto mb-8">
              Ready to elevate your family's style? Explore our collection and find 
              pieces that speak to your urban soul.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/shop">
                <Button className="btn-primary rounded-none min-w-[200px]" data-testid="shop-collection-btn">
                  Shop Collection
                </Button>
              </Link>
              <Link to="/drops">
                <Button variant="outline" className="btn-outline rounded-none min-w-[200px]" data-testid="view-drops-btn">
                  View Drops
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
